import type { Project, Testimonial, Service } from '../types';

import projectRealEstate from '/assets/aigc/images/portfolio-luxury-realestate_1774781295_000.png';
import projectSaasDashboard from '/assets/aigc/images/portfolio-saas-analytics_1774781294_000.png';
import projectFashion from '/assets/aigc/images/portfolio-fashion-brand_1774781296_000.png';
import projectRestaurant from '/assets/aigc/images/portfolio-restaurant-finedining_1774781295_000.png';
import projectFitness from '/assets/aigc/images/portfolio-fitness-landing_1774781311_000.png';
import projectCrypto from '/assets/aigc/images/portfolio-crypto-exchange_1774781312_000.png';

export const projects: Project[] = [
  {
    id: 'prestige-estates',
    title: 'Prestige Estates — Luxury Real Estate',
    description: 'A full-scale real estate platform built for a high-end property firm looking to attract affluent buyers with seamless browsing and elegant presentation.',
    fullDescription: 'We partnered with Prestige Estates to completely reimagine their digital presence. The previous site was outdated and lacked the sophistication their brand demanded. We delivered a dark, gold-accented interface featuring cinematic property photography, interactive listing cards, and an intuitive search experience. The result was a 47% increase in qualified leads within the first quarter after launch.',
    image: projectRealEstate,
    category: 'Website Design',
    technologies: ['Next.js', 'Tailwind CSS', 'Sanity CMS', 'Mapbox GL', 'Framer Motion'],
    goal: 'Create a luxury digital storefront that converts high-net-worth visitors into booked property viewings.',
  },
  {
    id: 'fintech-dashboard',
    title: 'FinTech Analytics Dashboard',
    description: 'A data-rich SaaS analytics dashboard designed for a fintech startup to track revenue, user growth, and conversion metrics in real time.',
    fullDescription: 'This fintech client needed an internal analytics platform that could rival enterprise tools but feel intuitive enough for non-technical stakeholders. We designed a clean, purple-accented dashboard with modular widget cards, real-time charting, geographic data visualization, and role-based access. The interface handles millions of data points while maintaining sub-second render times.',
    image: projectSaasDashboard,
    category: 'SaaS Platform',
    technologies: ['React', 'TypeScript', 'D3.js', 'Recharts', 'PostgreSQL', 'Supabase'],
    goal: 'Build a performant, visually clean analytics tool that simplifies complex data for executive decision-making.',
  },
  {
    id: 'noir-collection',
    title: 'NOIR Collection — Fashion E-Commerce',
    description: 'A minimalist, editorial-style e-commerce store for a premium fashion brand with a focus on typography and high-end product presentation.',
    fullDescription: 'NOIR Collection came to us wanting a digital flagship store that reflected the understated luxury of their brand. We stripped away every unnecessary element and built a monochromatic shopping experience that puts the product front and center. Featuring oversized editorial photography, seamless category filtering, and a one-page checkout flow. Their average order value increased by 32% post-launch.',
    image: projectFashion,
    category: 'E-Commerce',
    technologies: ['Shopify Hydrogen', 'React', 'Tailwind CSS', 'Stripe', 'Klaviyo'],
    goal: 'Deliver a high-conversion shopping experience that matches the brand\'s editorial identity and minimalist aesthetic.',
  },
  {
    id: 'maison-blanc',
    title: 'Maison Blanc — Fine Dining Experience',
    description: 'A dark, atmospheric website for a Michelin-starred restaurant featuring reservation integration, a curated gallery, and immersive food photography.',
    fullDescription: 'Maison Blanc approached us to create an online presence worthy of their Michelin star. We crafted an immersive, moody dark interface with warm amber accents that mirrors the intimate atmosphere of their dining room. The site features a stunning food photography gallery, seamless OpenTable reservation integration, seasonal menu displays, and a private events booking system. Reservations through the website tripled within two months.',
    image: projectRestaurant,
    category: 'Website Design',
    technologies: ['Next.js', 'GSAP', 'Prismic CMS', 'OpenTable API', 'Cloudinary'],
    goal: 'Build an atmospheric digital experience that captures the essence of fine dining and drives reservation bookings.',
  },
  {
    id: 'peak-performance',
    title: 'PEAK Performance — Fitness App Landing',
    description: 'A bold, high-energy landing page for a fitness tech startup launching a smartwatch-integrated wellness platform with app downloads and trial signups.',
    fullDescription: 'PEAK Performance needed a launch page that matched the intensity of their brand. We delivered a dark-themed, neon-green-accented design featuring an animated smartwatch hero, categorized workout modules, and social proof testimonials. The page was optimized for conversion with dual CTAs driving both app downloads and free trial signups. They hit 10,000 signups in the first week after launch.',
    image: projectFitness,
    category: 'Landing Page',
    technologies: ['React', 'Framer Motion', 'Tailwind CSS', 'Lottie', 'Vercel Analytics'],
    goal: 'Drive maximum app downloads and trial signups through a high-converting, visually striking landing page.',
  },
  {
    id: 'quantum-x',
    title: 'Quantum X — Crypto Trading Platform',
    description: 'A futuristic Web3 trading dashboard featuring real-time charts, portfolio management, and a trending tokens feed for a next-gen crypto exchange.',
    fullDescription: 'Quantum X came to us at a critical growth phase — they needed a trading interface that could compete with established exchanges while feeling fresh and accessible to newcomers. We designed a dark, glass-morphism-heavy dashboard with teal and cyan gradients, real-time BTC/ETH/SOL charting, a portfolio overview widget, asset management cards, and a trending tokens ticker. The new interface contributed to a 60% increase in daily active traders.',
    image: projectCrypto,
    category: 'SaaS Platform',
    technologies: ['Next.js', 'WebSocket', 'TradingView Charts', 'Web3.js', 'Solana SDK'],
    goal: 'Create a professional-grade trading interface that attracts both experienced traders and crypto newcomers.',
  },
];

export const services: Service[] = [
  {
    id: 'web-design',
    title: 'Website Design',
    description: 'Custom, conversion-focused websites built from scratch. Every pixel is intentional, every interaction is designed to move visitors toward action.',
    icon: 'monitor',
  },
  {
    id: 'ui-ux',
    title: 'UI/UX Design',
    description: 'Research-driven interface design that balances visual beauty with usability. We design experiences that feel intuitive from the first click.',
    icon: 'layers',
  },
  {
    id: 'landing-pages',
    title: 'Landing Pages',
    description: 'High-converting landing pages engineered for results. From lead generation to product launches, every element is optimized for maximum impact.',
    icon: 'rocket',
  },
  {
    id: 'saas-platforms',
    title: 'SaaS Platforms',
    description: 'End-to-end design for software products. Dashboards, onboarding flows, and feature-rich interfaces that users actually enjoy working with.',
    icon: 'grid',
  },
];

export const testimonials: Testimonial[] = [
  {
    id: '1',
    name: 'Marcus Chen',
    role: 'CEO',
    company: 'Prestige Estates',
    quote: 'They completely transformed our digital presence. The new website feels exactly like walking into one of our luxury properties — refined, elegant, and impossible to ignore. Leads went up 47% in the first quarter.',
    avatar: 'MC',
  },
  {
    id: '2',
    name: 'Sarah Lindqvist',
    role: 'Product Lead',
    company: 'FinTrack Analytics',
    quote: 'Working with this team was effortless. They took a complex data platform and made it beautiful without sacrificing any functionality. Our stakeholders actually enjoy checking dashboards now.',
    avatar: 'SL',
  },
  {
    id: '3',
    name: 'James Okoye',
    role: 'Founder',
    company: 'PEAK Performance',
    quote: 'We needed a launch page that could generate serious momentum. They delivered something we were proud to show investors. 10,000 signups in week one speaks for itself.',
    avatar: 'JO',
  },
  {
    id: '4',
    name: 'Elena Morozova',
    role: 'Creative Director',
    company: 'NOIR Collection',
    quote: 'Most agencies struggle with minimalism — they confuse it with emptiness. This team understood restraint. Our e-commerce store feels like stepping into a gallery. AOV increased by 32%.',
    avatar: 'EM',
  },
];

export interface FaqItem {
  question: string;
  answer: string;
}

export const faqItems: FaqItem[] = [
  {
    question: 'What types of projects do you take on?',
    answer: 'We specialize in premium websites, SaaS platforms, e-commerce stores, and high-converting landing pages. Whether you\'re a startup launching your first product or an established brand looking to level up — if it involves the web and it needs to look incredible, we\'re in.',
  },
  {
    question: 'How long does a typical project take?',
    answer: 'Most projects are delivered within 3–6 weeks from kickoff to launch. Simple landing pages can be ready in as little as 1–2 weeks, while complex platforms with custom features may take 8–12 weeks. We move fast without cutting corners.',
  },
  {
    question: 'What does the process look like from start to finish?',
    answer: 'It starts with a discovery call to understand your goals. From there we move into strategy and wireframing, then visual design, development, and testing — all in close collaboration with you. We handle everything, and you\'ll see progress at every stage.',
  },
  {
    question: 'Do you build the site or just design it?',
    answer: 'We do both. Every project is designed and developed in-house using modern frameworks like React, Next.js, and Tailwind CSS. You get a fully functional, production-ready website — not just a Figma file.',
  },
  {
    question: 'How much does a project cost?',
    answer: 'Projects typically range from $3,000 to $15,000+ depending on scope and complexity. We offer flexible packages to fit different budgets. The best way to get an accurate estimate is to fill out our contact form and tell us about your project.',
  },
  {
    question: 'Do you offer ongoing maintenance after launch?',
    answer: 'Absolutely. We offer monthly retainer packages that cover performance monitoring, content updates, security patches, and ongoing design improvements. We treat your site as a living product, not a one-time deliverable.',
  },
  {
    question: 'Can you work with our existing brand guidelines?',
    answer: 'Yes — we frequently work within existing brand systems. Whether you have a full brand book or just a logo and color palette, we\'ll extend your identity into a cohesive digital experience that feels on-brand at every touchpoint.',
  },
  {
    question: 'What if I don\'t have content ready?',
    answer: 'No problem. We can guide content strategy and provide copywriting direction. Many of our clients come to us with rough ideas, and we help shape the messaging. We also work with trusted copywriters we can bring in if needed.',
  },
];
